import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthContext } from '../contexts/AuthContext';
import { useEvents } from '../hooks/useFirebase';
import { getLimits, formatMax } from '../utils/planLimits';
import { useOwnerLicenses, isOwnerLicenseValid } from '../hooks/useOwnerLicenses';
import EventList from '../components/EventList';
import EventPage from './EventPage';
import MatchingPage from './MatchingPage';
import UsersPage from './admin/UsersPage';
import StaffPage from './StaffPage';
import ScheduleManager from '../components/ScheduleManager';
import ApplicationsView from '../components/ApplicationsView';
import { useApplicationForm } from '../hooks/useApplicationForm';

const EVENT_TYPE_DEFS = [
  {
    value: 'matching',
    icon: '🎭',
    label: 'キャスト型',
    desc: 'キャストとゲストのマッチング',
    detail: 'VRChatのキャストイベント向け。キャストとゲストを登録し、希望に基づいてマッチングします。日程管理・出勤管理も利用できます。',
  },
  {
    value: 'raffle',
    icon: '🎲',
    label: '抽選型',
    desc: '定員から抽選で当選者を選出',
    detail: 'バー・個人イベント向け。定員を設定し、応募者の中からランダムに当選者を選びます。キャストのマッチングはありませんが、日程管理・出退勤管理は利用できます。',
  },
  {
    value: 'open',
    icon: '📋',
    label: '先着型',
    desc: '抽選なし・応募順に受付',
    detail: '抽選なし。定員に達するまで応募順に受け付けます。先着制のイベントや参加登録のみに使えます。日程管理・出退勤管理も利用できます。',
  },
  {
    value: 'cast_instance',
    icon: '🏘️',
    label: 'インスタンス型',
    desc: 'キャストをインスタンスへ割り振り',
    detail: 'ゲスト出入り自由のインスタンス向け。キャストの出欠を管理し、ランダムに各インスタンスへ割り振ります。部制にも対応しています。',
  },
];

const DashboardLayout = () => {
  const { logout } = useAuth();
  const { user, userProfile, isSuperAdmin, isOwner, isStaff, isLicenseExpired } = useAuthContext();
  const navigate = useNavigate();
  const { events, createEvent, deleteEvent } = useEvents();

  // スタッフユーザー用: 所属イベントのオーナーライセンスをチェック
  const staffEventOwnerIds = useMemo(() => {
    if (!isStaff || !user?.uid) return [];
    return [...new Set(
      Object.values(events)
        .filter(e => e.staff_members?.[user.uid])
        .map(e => e.owner_id)
        .filter(Boolean)
    )];
  }, [isStaff, events, user?.uid]);

  const ownerLicenses = useOwnerLicenses(staffEventOwnerIds);

  const [activeTab, setActiveTab] = useState('events');
  const [activeEventTab, setActiveEventTab] = useState('manage'); // 'manage' | 'matching' | 'schedule'
  const [hoveredEventTab, setHoveredEventTab] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newEventName, setNewEventName] = useState('');
  const [newMatchingMode, setNewMatchingMode] = useState('cast_select');
  const [newEventType, setNewEventType] = useState('matching');
  const [newCapacity, setNewCapacity] = useState('');
  const [newInstanceMode, setNewInstanceMode] = useState('count'); // 'count' | 'capacity'
  const [newInstanceCount, setNewInstanceCount] = useState('');
  const [newInstanceCapacity, setNewInstanceCapacity] = useState('');
  const [showTypeInfo, setShowTypeInfo] = useState(null); // 'matching'|'raffle'|'open'|'cast_instance'|null
  const [showLimitTooltip, setShowLimitTooltip] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const { applications, formDef, formToken } = useApplicationForm(selectedEventId);

  // プラン制限
  const limits = getLimits(userProfile?.license?.plan);

  // ロール別タブ定義（ライセンス期限切れownerはスタッフ管理タブを非表示）
  const TABS = [
    { id: 'events', label: '📋 イベント管理' },
    ...(isSuperAdmin ? [{ id: 'users', label: '👥 ユーザー管理' }] : []),
    ...(isSuperAdmin || (isOwner && !isLicenseExpired) ? [{ id: 'staff', label: '👤 スタッフ管理' }] : []),
  ];

  // ロール別イベントフィルタリング
  const filteredEvents = Object.entries(events).reduce((acc, [key, event]) => {
    if (isSuperAdmin) {
      acc[key] = event;
    } else if (isOwner) {
      if (isLicenseExpired) {
        // 期限切れ: スタッフ参加イベントのみ表示（自分のイベントは除外）
        if (event.staff_members?.[user.uid]) acc[key] = event;
      } else {
        if (event.owner_id === user.uid || event.staff_members?.[user.uid]) acc[key] = event;
      }
    } else if (isStaff) {
      // staff: オーナーのライセンスが有効なイベントのみ表示
      if (event.staff_members?.[user.uid] && isOwnerLicenseValid(ownerLicenses[event.owner_id])) {
        acc[key] = event;
      }
    }
    return acc;
  }, {});

  // ownerが管理するイベント（スタッフ管理タブ用・自分がowner_idのもののみ）
  const ownedEvents = isSuperAdmin
    ? filteredEvents
    : Object.fromEntries(
        Object.entries(filteredEvents).filter(([, e]) => e.owner_id === user.uid)
      );

  // ownerのイベント数・上限チェック（所有イベントのみカウント）
  const ownerEventCount = Object.keys(ownedEvents).length;
  const isEventLimitReached = isOwner && ownerEventCount >= limits.maxEvents;


  const handleOpenCreateModal = () => {
    if (isEventLimitReached) return; // ボタン側でも防ぐが念のため
    setIsCreateModalOpen(true);
  };

  const needsCapacity     = newEventType === 'raffle' || newEventType === 'open';
  const needsInstance     = newEventType === 'cast_instance';
  const capacityValid     = !needsCapacity || (newCapacity !== '' && Number(newCapacity) >= 1);
  const instanceValid     = !needsInstance || (
    newInstanceMode === 'count'
      ? (newInstanceCount !== '' && Number(newInstanceCount) >= 1)
      : (newInstanceCapacity !== '' && Number(newInstanceCapacity) >= 1)
  );
  const nameValid         = newEventName.trim() !== '';
  const canCreate         = nameValid && capacityValid && instanceValid;

  const handleConfirmCreate = () => {
    if (!canCreate) { setSubmitAttempted(true); return; }
    const instanceOptions = needsInstance
      ? { mode: newInstanceMode, count: newInstanceCount, capacity: newInstanceCapacity }
      : null;
    createEvent(newEventName, newMatchingMode, user.uid, newEventType, newCapacity, instanceOptions);
    setIsCreateModalOpen(false);
    setNewEventName('');
    setNewMatchingMode('cast_select');
    setNewEventType('matching');
    setNewCapacity('');
    setNewInstanceMode('count');
    setNewInstanceCount('');
    setNewInstanceCapacity('');
    setShowTypeInfo(null);
    setSubmitAttempted(false);
  };

  const renderContent = () => {
    if (activeTab === 'users' && isSuperAdmin) {
      return <div style={styles.scrollPane}><UsersPage /></div>;
    }

    if (activeTab === 'staff' && (isSuperAdmin || isOwner)) {
      return <div style={styles.scrollPane}><StaffPage events={ownedEvents} /></div>;
    }

    if (!selectedEventId) {
      return (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>👈</div>
          <h2>イベントを選択してください</h2>
          <p>左側のリストからイベントを選ぶか、新しく作成してください。</p>
        </div>
      );
    }

    // イベント選択済み：上部タブ + コンテンツ
    return (
      <div style={styles.eventContentWrap}>
        <div style={styles.eventTabs}>
          {[
            { id: 'manage',       label: '⚙️ イベント管理' },
            { id: 'schedule',     label: '📅 日程管理' },
            ...(events[selectedEventId]?.event_type !== 'cast_instance'
              ? [{ id: 'applications', label: '📝 応募状況' }]
              : []),
            { id: 'matching',     label: '🎲 抽選実行' },
          ].map((t) => {
            const isActive = activeEventTab === t.id;
            const isHovered = hoveredEventTab === t.id && !isActive;
            return (
              <button
                key={t.id}
                onClick={() => setActiveEventTab(t.id)}
                onMouseEnter={() => setHoveredEventTab(t.id)}
                onMouseLeave={() => setHoveredEventTab(null)}
                style={{
                  ...styles.eventTabButton,
                  background: 'transparent',
                  color: isActive ? '#6c63ff' : isHovered ? '#475569' : '#94a3b8',
                  fontWeight: isActive ? '800' : '600',
                  borderBottom: isActive ? '2px solid #6c63ff' : '2px solid transparent',
                  borderRadius: 0,
                  paddingBottom: '11px',
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <div style={styles.eventTabContent}>
          {activeEventTab === 'manage'       && <EventPage selectedEventId={selectedEventId} onNavigateToMatching={() => setActiveEventTab('matching')} />}
          {activeEventTab === 'matching'     && <MatchingPage selectedEventId={selectedEventId} />}
          {activeEventTab === 'schedule'     && <ScheduleManager eventId={selectedEventId} />}
          {activeEventTab === 'applications' && <ApplicationsView applications={applications} formDef={formDef} event={events[selectedEventId]} formToken={formToken} />}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.root}>
      {/* Sidebar */}
      <div style={styles.sidebar}>
        {/* ロゴ（固定） */}
        <div style={styles.sidebarHeader}>
          <div style={styles.logo}>
            <img src="/logo.png" alt="LinkBoard" style={{ height: 28, width: 'auto' }} />
            <span style={styles.logoText}>LinkBoard</span>
          </div>

          <div style={styles.nav}>
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    ...styles.navButton,
                    background: isActive ? '#6c63ff' : 'transparent',
                    color: isActive ? '#fff' : '#64748b',
                    boxShadow: isActive ? '0 2px 8px rgba(108,99,255,0.35)' : 'none',
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* イベントリスト領域（常にflex:1で空間確保、中身だけ切替） */}
        <div style={styles.sidebarEvents}>
          {activeTab === 'events' && <>
          <div style={styles.sidebarEventsHeader}>
            <span style={styles.sidebarEventsTitle}>Events</span>
            {(isSuperAdmin || (isOwner && !isLicenseExpired)) && (
              <div
                style={styles.addButtonWrap}
                onMouseEnter={() => isEventLimitReached && setShowLimitTooltip(true)}
                onMouseLeave={() => setShowLimitTooltip(false)}
              >
                <button
                  onClick={handleOpenCreateModal}
                  style={{
                    ...styles.addButton,
                    opacity: isEventLimitReached ? 0.4 : 1,
                    cursor: isEventLimitReached ? 'not-allowed' : 'pointer',
                  }}
                  disabled={isEventLimitReached}
                >
                  +
                </button>
                {showLimitTooltip && (
                  <div style={styles.addButtonTooltip}>
                    上限（{limits.maxEvents}件）に達しています
                    <span style={styles.addButtonTooltipSub}>Proプランで無制限に作成できます</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* イベントリスト（ここだけスクロール） */}
          <div style={styles.eventListScroll}>
            <EventList
              events={filteredEvents}
              selectedEventId={selectedEventId}
              onSelect={setSelectedEventId}
              onDelete={isSuperAdmin || isOwner ? deleteEvent : undefined}
              canDelete={(eventId) =>
                isSuperAdmin || filteredEvents[eventId]?.owner_id === user.uid
              }
            />
          </div>
          </>}
        </div>

        {/* ユーザー情報＆ログアウト（常に最下部固定） */}
        <div style={styles.userProfile}>
          {/* イニシャルアバター */}
          <div style={styles.userAvatar}>
            {(userProfile?.display_name || user?.email || '?').charAt(0).toUpperCase()}
          </div>
          <div style={styles.userInfo}>
            <div style={styles.userName}>{userProfile?.display_name || user?.email}</div>
            <div style={styles.userRole}>{userProfile?.role}</div>
          </div>
          <button onClick={() => navigate('/portal')} style={styles.logoutButton} title="マイポータル">
            🏠
          </button>
          <button onClick={() => navigate('/account', { state: { from: '/dashboard' } })} style={styles.logoutButton} title="アカウント設定">
            ⚙️
          </button>
          <button onClick={logout} style={styles.logoutButton} title="ログアウト">
            ↩
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={styles.main}>
        {isOwner && isLicenseExpired && (
          <div style={styles.licenseBanner}>
            ⚠️ ライセンスの有効期限が切れています。自分のイベントとスタッフ管理は無効化されています。管理者にお問い合わせください。
          </div>
        )}
        {renderContent()}
      </div>

      {/* 新規イベント作成モーダル */}
      {isCreateModalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>新規イベント作成</h2>

            {isOwner && (
              <p style={styles.usageNote}>
                イベント: {ownerEventCount} / {formatMax(limits.maxEvents)} 件
              </p>
            )}

            <label style={styles.modalLabel}>イベント名</label>
            <input
              style={{ ...styles.modalInput, ...(submitAttempted && !nameValid ? { borderColor: '#ef4444', background: '#fff5f5' } : {}) }}
              value={newEventName}
              onChange={(e) => { setNewEventName(e.target.value); }}
              placeholder="例：第1回 〇〇イベント"
            />

            {/* ── イベントタイプ選択 ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <label style={{ ...styles.modalLabel, margin: 0 }}>イベントタイプ</label>
              <button
                onClick={() => setShowTypeInfo(showTypeInfo ? null : newEventType)}
                style={{ background: 'none', border: '1.5px solid #c4b5fd', borderRadius: '50%', width: 18, height: 18, fontSize: 10, fontWeight: 800, color: '#6c63ff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }}
              >?</button>
            </div>
            {/* タイプ説明ポップアップ */}
            {showTypeInfo && (() => {
              const info = EVENT_TYPE_DEFS.find(d => d.value === showTypeInfo);
              return info ? (
                <div style={{ background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#4c1d95', lineHeight: 1.6 }}>
                  <strong>{info.icon} {info.label}</strong><br />{info.detail}
                </div>
              ) : null;
            })()}
            {/* タイプカード */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {EVENT_TYPE_DEFS.map((def) => {
                const isActive = newEventType === def.value;
                return (
                  <button
                    key={def.value}
                    onClick={() => { setNewEventType(def.value); setShowTypeInfo(def.value); setNewCapacity(''); setNewInstanceCount(''); setNewInstanceCapacity(''); setNewInstanceMode('count'); }}
                    style={{
                      flex: 1, minWidth: 90,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                      padding: '10px 8px',
                      border: `2px solid ${isActive ? '#6c63ff' : '#e2e8f0'}`,
                      borderRadius: 10,
                      background: isActive ? '#f5f3ff' : '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ fontSize: 20 }}>{def.icon}</span>
                    <span style={{ fontSize: 12, fontWeight: 800, color: isActive ? '#6c63ff' : '#334155' }}>{def.label}</span>
                    <span style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', lineHeight: 1.3 }}>{def.desc}</span>
                  </button>
                );
              })}
            </div>

            {/* matching: マッチング方式 */}
            {newEventType === 'matching' && (
              <>
                <label style={styles.modalLabel}>マッチング方式</label>
                <select
                  style={styles.modalSelect}
                  value={newMatchingMode}
                  onChange={(e) => setNewMatchingMode(e.target.value)}
                >
                  <option value="cast_select">キャスト選択 (キャストがゲストを選ぶ)</option>
                  <option value="guest_select">ゲスト選択 (ゲストがキャストを選ぶ)</option>
                  <option value="both_select">両想い (お互いの希望を考慮してマッチング)</option>
                  <option value="random_only">ランダム抽選</option>
                </select>
              </>
            )}

            {/* raffle / open: 定員 */}
            {(newEventType === 'raffle' || newEventType === 'open') && (
              <>
                <label style={styles.modalLabel}>定員（人）</label>
                <input
                  type="number"
                  min="1"
                  style={{ ...styles.modalInput, ...(submitAttempted && !capacityValid ? { borderColor: '#ef4444', background: '#fff5f5' } : {}) }}
                  value={newCapacity}
                  onChange={(e) => setNewCapacity(e.target.value)}
                  onKeyDown={(e) => ['e','E','+','-','.'].includes(e.key) && e.preventDefault()}
                  placeholder="例：20"
                />
              </>
            )}

            {/* cast_instance: インスタンス設定 */}
            {newEventType === 'cast_instance' && (
              <>
                <label style={styles.modalLabel}>インスタンス設定</label>
                <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                  {[
                    { value: 'count',    label: 'インスタンス数で指定' },
                    { value: 'capacity', label: '1インスタンスの人数で指定' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setNewInstanceMode(opt.value)}
                      style={{
                        flex: 1, padding: '7px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                        border: `1.5px solid ${newInstanceMode === opt.value ? '#6c63ff' : '#e2e8f0'}`,
                        background: newInstanceMode === opt.value ? '#f5f3ff' : '#fff',
                        color: newInstanceMode === opt.value ? '#6c63ff' : '#64748b',
                      }}
                    >{opt.label}</button>
                  ))}
                </div>
                {newInstanceMode === 'count' ? (
                  <input
                    type="number" min="1"
                    style={{ ...styles.modalInput, ...(submitAttempted && !instanceValid ? { borderColor: '#ef4444', background: '#fff5f5' } : {}) }}
                    value={newInstanceCount}
                    onChange={(e) => setNewInstanceCount(e.target.value)}
                    onKeyDown={(e) => ['e','E','+','-','.'].includes(e.key) && e.preventDefault()}
                    placeholder="例：3（インスタンスを3つ作成）"
                  />
                ) : (
                  <input
                    type="number" min="1"
                    style={{ ...styles.modalInput, ...(submitAttempted && !instanceValid ? { borderColor: '#ef4444', background: '#fff5f5' } : {}) }}
                    value={newInstanceCapacity}
                    onChange={(e) => setNewInstanceCapacity(e.target.value)}
                    onKeyDown={(e) => ['e','E','+','-','.'].includes(e.key) && e.preventDefault()}
                    placeholder="例：5（1インスタンスあたり5人）"
                  />
                )}
              </>
            )}

            <div style={styles.modalActions}>
              <button onClick={() => { setIsCreateModalOpen(false); setSubmitAttempted(false); }} style={styles.secondaryButton}>
                キャンセル
              </button>
              <button
                onClick={handleConfirmCreate}
                style={{ ...styles.primaryButtonSmall, opacity: canCreate ? 1 : 0.45 }}
              >
                イベントを作成
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  root: {
    display: 'flex',
    height: '100vh',
    background: '#f8f9fd',
    fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
  },
  sidebar: {
    width: '280px',
    background: '#0f172a',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '4px 0 10px rgba(0,0,0,0.1)',
  },
  sidebarHeader: {
    padding: '24px 16px 16px',
    flexShrink: 0,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    marginBottom: '32px',
  },
  logoIcon: { fontSize: '28px' },
  logoText: {
    fontSize: '18px',
    fontWeight: '800',
    color: '#fff',
    letterSpacing: '0.5px',
  },
  nav: { display: 'flex', flexDirection: 'column', gap: '8px' },
  navButton: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.15s',
    width: '100%',
  },
  sidebarEvents: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderTop: '1px solid rgba(255,255,255,0.05)',
    padding: '16px 12px 0',
    minHeight: 0,
  },
  eventListScroll: {
    flex: 1,
    overflowY: 'auto',
    paddingBottom: '16px',
    paddingRight: '4px',
    scrollbarGutter: 'stable',
  },
  sidebarEventsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    padding: '0 12px',
  },
  sidebarEventsTitle: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  addButton: {
    background: '#6c63ff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  addButtonWrap: {
    position: 'relative',
    display: 'inline-block',
  },
  addButtonTooltip: {
    position: 'absolute',
    top: '30px',
    right: 0,
    background: '#1e293b',
    color: '#fff',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  },
  addButtonTooltipSub: {
    fontSize: '11px',
    color: '#94a3b8',
    fontWeight: '400',
  },
  eventContentWrap: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  eventTabs: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '0',
    background: '#ffffff',
    borderBottom: '1px solid #e8edf5',
    padding: '0 28px',
    flexShrink: 0,
  },
  eventTabButton: {
    padding: '14px 20px 0',
    border: 'none',
    fontSize: '13.5px',
    cursor: 'pointer',
    letterSpacing: '0.2px',
    transition: 'color 0.15s, border-color 0.15s',
    userSelect: 'none',
    background: 'transparent',
  },
  eventTabContent: {
    flex: 1,
    overflow: 'auto',
  },
  userProfile: {
    padding: '12px 16px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  userAvatar: {
    width: '32px', height: '32px', borderRadius: '8px',
    background: '#6c63ff',
    color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '14px', fontWeight: '800', flexShrink: 0,
  },
  userInfo: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 },
  userName: { color: '#e2e8f0', fontSize: '13px', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  userRole: { color: '#475569', fontSize: '11px', marginTop: '1px' },
  logoutButton: {
    background: 'transparent',
    color: '#475569',
    border: 'none',
    borderRadius: '6px',
    padding: '6px 8px',
    fontSize: '16px',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'color 0.15s',
    lineHeight: 1,
  },
  main: { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  licenseBanner: {
    background: '#fff7ed',
    borderBottom: '1px solid #fed7aa',
    color: '#9a3412',
    fontSize: '13px',
    fontWeight: '600',
    padding: '10px 24px',
    flexShrink: 0,
  },
  scrollPane: { flex: 1, overflowY: 'auto', height: '100%' },
  emptyState: { textAlign: 'center', marginTop: '100px', color: '#64748b' },
  emptyIcon: { fontSize: '64px', marginBottom: '16px' },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)',
  },
  modalContent: {
    background: '#fff',
    padding: '32px',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    margin: '0 20px',
  },
  modalTitle: { fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: '0 0 4px 0' },
  usageNote: { fontSize: '13px', color: '#6c63ff', fontWeight: '600', margin: '0 0 16px 0' },
  modalLabel: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '700',
    color: '#475569',
    marginBottom: '8px',
    marginTop: '20px',
  },
  modalSubText: { fontSize: '14px', color: '#64748b', marginBottom: '12px' },
  modalInput: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '16px',
    marginBottom: '24px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  modalSelect: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '15px',
    marginBottom: '24px',
    background: '#fff',
    outline: 'none',
    cursor: 'pointer',
  },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '12px' },
  primaryButtonSmall: {
    background: '#6c63ff',
    color: '#fff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  secondaryButton: {
    background: '#f1f5f9',
    color: '#475569',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default DashboardLayout;
